/**
 * AskX1ContextProvider.tsx — page-context for the Ask X1 widget.
 *
 * Entity pages call `useAskX1PageContext({ type, id, label })` so the floating
 * widget knows what the user is currently viewing (a wallet, validator, token,
 * transaction, NFT, or program). The context is set on mount and cleared on
 * unmount, so navigating away resets it. The widget reads it via
 * `useAskX1Context()` — no prop-drilling.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type AskX1EntityType =
  | 'wallet'
  | 'validator'
  | 'transaction'
  | 'token'
  | 'nft'
  | 'program'

export interface AskX1PageContext {
  type: AskX1EntityType
  id: string
  label?: string
}

interface AskX1ContextValue {
  pageContext: AskX1PageContext | null
  setPageContext: (c: AskX1PageContext | null) => void
}

const AskX1Ctx = createContext<AskX1ContextValue>({
  pageContext: null,
  setPageContext: () => {},
})

export function AskX1ContextProvider({ children }: { children: ReactNode }) {
  const [pageContext, setPageContext] = useState<AskX1PageContext | null>(null)
  return (
    <AskX1Ctx.Provider value={{ pageContext, setPageContext }}>
      {children}
    </AskX1Ctx.Provider>
  )
}

/** Read the current page context (used by the widget). */
export function useAskX1Context(): AskX1ContextValue {
  return useContext(AskX1Ctx)
}

/**
 * Register the entity the current page is about. Pass `null` to register nothing.
 * Sets on mount / when the entity changes, and clears on unmount.
 */
export function useAskX1PageContext(ctx: AskX1PageContext | null | undefined): void {
  const { setPageContext } = useAskX1Context()
  const type = ctx?.type
  const id = ctx?.id
  const label = ctx?.label
  useEffect(() => {
    if (type && id) setPageContext({ type, id, label })
    else setPageContext(null)
    return () => setPageContext(null)
  }, [type, id, label, setPageContext])
}
